import * as React from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { 
  FormControl, 
  InputAdornment, 
  InputLabel, 
  MenuItem, 
  OutlinedInput,
  Stack, 
  Badge, 
  IconButton,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CartDrawer from './CartDrawer.tsx';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext.tsx';
import { obter_grupos, obter_subgrupos, obter_divisoes } from '../services/filters-api.js';

type Grupo = {
  codigo: number;
  nome: string;
};

type Subgrupo = {
  codigo: number;
  nome: string;
};

type Divisao = {
  codigo: number;
  nome: string;
};

type HeaderProps = {
  onSearch: (term: string) => void;
  onFilterChange: (filters : { grupo?: string; subgrupo?: string; divisao?: string; }) => void;
  currentFilters: { grupo: string, subgrupo: string, divisao: string };
};

export default function HeaderRes({ onSearch, onFilterChange, currentFilters }: HeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const [openCart, setOpenCart] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  // eslint-disable-next-line
  const [loading, setLoading] = React.useState({
    grupos: false,
    subgrupos: false,
    divisoes: false
  });
  
  const [grupos, setGrupos] = React.useState<Grupo[]>([]);
  const [subgrupos, setSubgrupos] = React.useState<Subgrupo[]>([]);
  const [divisoes, setDivisoes] = React.useState<Divisao[]>([]);

  const carregarGrupos = async () => {
    try {
      setLoading(prev => ({...prev, grupos: true }));
      const gruposAPI = await obter_grupos();
      setGrupos(gruposAPI);
    } catch (err) {
      console.error('Erro ao carregar os grupos:', err);
    } finally{
      setLoading(prev => ({...prev, grupos: false }));
    }
  };

  const carregarSubgrupos = async () => {
    try {
      setLoading(prev => ({...prev, subgrupos: true }));
      const subgruposAPI = await obter_subgrupos(parseInt(currentFilters.grupo));
      setSubgrupos(subgruposAPI);
      onFilterChange({ subgrupo: '', divisao: '' });
    } catch (err) {
      console.error('Erro ao carregar os grupos:', err);
    } finally{
      setLoading(prev => ({...prev, subgrupos: false }));
    }
  };

  const carregarDivisoes =  async () => {
    try {
      setLoading(prev => ({ ...prev, divisoes: true }));
      const divisoesAPI = await obter_divisoes( parseInt(currentFilters.grupo), parseInt(currentFilters.subgrupo));
      setDivisoes(divisoesAPI);
      onFilterChange({ divisao: '' });
    } catch (error) {
      console.error('Erro ao carregar divisões:', error);
    } finally {
      setLoading(prev => ({ ...prev, divisoes: false }));
    }
  };

  React.useEffect(() => {
    carregarGrupos();
  }, []);

  React.useEffect(() => {
    if(!currentFilters.grupo){
      setSubgrupos([]);
      return;
    }
    carregarSubgrupos();
  }, [currentFilters.grupo]);

  React.useEffect(() => {
    if (!currentFilters.subgrupo || !currentFilters.grupo) {
      setDivisoes([]);
      return;
    }
    carregarDivisoes();
  }, [currentFilters.subgrupo, currentFilters.grupo]);

  const toggleDrawer = (newOpenCart: boolean) => {
    setOpenCart(newOpenCart);
  };  

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    onSearch(term); 
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch(searchTerm);
  };

  const handleGrupoChange = (e: SelectChangeEvent) => {
    onFilterChange({ grupo: e.target.value });
  };

  const handleSubgrupoChange = (e: SelectChangeEvent) => {
    onFilterChange({ subgrupo: e.target.value });
  };

  const handleDivisaoChange = (e: SelectChangeEvent) => {
    onFilterChange({ divisao: e.target.value });
  };

  const cleanFilters = () => {
    onFilterChange({ grupo: '', subgrupo: '', divisao: '' });
    setSearchTerm('');
    onSearch('');
  };

  const totalItems = cartItems.reduce((total, item) => total + item.quantidade_item, 0);

  return (
    <>
      <Box
        sx={{ 
          px: 2, 
          pt: 2, 
          pb: isMobile ? 2 : 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          marginTop: { xs: 4, sm: 4, md: 0, xl: 0}, 
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <FormControl 
            component="form" 
            onSubmit={handleSearchSubmit} 
            sx={{ 
              flexGrow: 1,
              minWidth: isMobile ? '100%' : '200px',
              order: isMobile ? 1 : 0
            }} 
            variant="outlined"
          >
            <OutlinedInput
              size="medium"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Pesquisar..."
              startAdornment={
                <InputAdornment position="start" sx={{ color: 'text.primary' }}>
                  <SearchRoundedIcon fontSize="medium" />
                </InputAdornment>
              }
              inputProps={{
                'aria-label': 'Pesquisar...',
              }}
            />
          </FormControl>

        </Box>

        {/* Filtros */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 1,
            width: '100%'
          }}
        >
          {/* Grupo */}
          <FormControl fullWidth={isMobile} size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Grupo</InputLabel>
            <Select
              value={currentFilters.grupo}
              label="Grupo"
              onChange={handleGrupoChange}
            >
              {grupos.map((g) => (
                <MenuItem key={g.codigo} value={g.codigo.toString()}>
                  {g.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Subgrupo */}
          <FormControl 
            fullWidth={isMobile} 
            size="small" 
            sx={{ minWidth: isMobile ? '100%' : 150 }} 
            disabled={!currentFilters.grupo}
          >
            <InputLabel>Subgrupo</InputLabel>
            <Select
              value={currentFilters.subgrupo}
              label="Subgrupo"
              onChange={handleSubgrupoChange}
            >
              {subgrupos.map((g) => (
                <MenuItem key={g.codigo} value={g.codigo.toString()}>
                  {g.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Divisao */}
          <FormControl 
            fullWidth={isMobile} 
            size="small" 
            sx={{ minWidth: isMobile ? '100%' : 150 }} 
            disabled={!currentFilters.grupo}
          >
            <InputLabel>Divisão</InputLabel>
            <Select
              value={currentFilters.divisao}
              label="Divisão"
              onChange={handleDivisaoChange}
            >
              {divisoes.map((g) => (
                <MenuItem key={g.codigo} value={g.codigo.toString()}>
                  {g.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Stack 
            direction="row" 
            spacing={1}
            sx={{
              order: isMobile ? 0 : 1,
              ml: isMobile ? 'auto' : 0
            }}
          >
            <IconButton aria-label="limpar filtros" onClick={() => cleanFilters()}>
              <FilterAltOffIcon />
            </IconButton>
            <IconButton aria-label="usuário" onClick={() => navigate('/configuracoes')}>
              <SettingsIcon />
            </IconButton>
            <IconButton aria-label="carrinho" onClick={() => toggleDrawer(true)}>
              <Badge badgeContent={totalItems} color="success">
                <LocalGroceryStoreIcon />
              </Badge>
            </IconButton>
          </Stack>
      </Box>

      <CartDrawer open={openCart} onClose={() => toggleDrawer(false)} />
    </>
  );
}